import { cn } from "@core-ui/utils/cn";
import EditMenu from "@ext/item/EditMenu";
import t from "@ext/localization/locale/translate";
import { useParentNavigationControl } from "@ext/navigation/catalog/SidebarNavigation/hooks/useParentNavigationControl";
import { useNavigationTreeStore } from "@ext/navigation/catalog/SidebarNavigation/store/navigationTreeStore";
import type { ItemLink } from "@ext/navigation/NavigationLinks";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@ui-kit/Dropdown";
import { Icon } from "@ui-kit/Icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "@ui-kit/Tooltip";
import { useRef, useState } from "react";

interface NavigationTreeItemActionsProps {
	itemLink?: ItemLink;
	onAddChild?: () => void;
}

export const NavigationTreeItemActions = ({ itemLink, onAddChild }: NavigationTreeItemActionsProps) => {
	const [currentItemLink, setCurrentItemLink] = useState<ItemLink>(itemLink);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const markerRef = useRef<HTMLSpanElement>(null);
	const isDndActive = useNavigationTreeStore((state) => state.draggingId !== null);
	const isParentActive = useParentNavigationControl(markerRef, isDropdownOpen);
	const shouldRender = isParentActive && (!isDndActive || isDropdownOpen);

	if (!shouldRender) return <span aria-hidden className="hidden" ref={markerRef} />;

	const moreButton = (
		<button
			aria-label={t("article.actions.title")}
			className="hover:bg-sidebar-accent group/actions m-0 flex size-5 appearance-none items-center justify-center rounded border-0 bg-transparent p-0 text-left text-muted-foreground"
			data-testid="article-actions"
			type="button"
		>
			<Icon className="group-hover/actions:text-primary-fg" icon="more-horizontal" />
		</button>
	);

	const moreButtonTooltipTrigger = <TooltipTrigger asChild>{moreButton}</TooltipTrigger>;

	// the dropdown trigger has to stay outside the tooltip trigger: the outermost Radix trigger
	// wins the shared `data-state` attribute, and `.right-extensions` keys its visibility off it
	const moreButtonWithTooltip = (
		<Tooltip>
			{itemLink ? (
				<DropdownMenuTrigger asChild>{moreButtonTooltipTrigger}</DropdownMenuTrigger>
			) : (
				moreButtonTooltipTrigger
			)}
			<TooltipContent focus="high">{t("article.actions.title")}</TooltipContent>
		</Tooltip>
	);

	return (
		<span
			className={cn(
				"right-extensions ml-auto flex w-0 shrink-0 items-center gap-1 overflow-hidden opacity-0",
				!isDndActive && "group-hover/nav:w-auto group-hover/nav:opacity-100",
				"[&:has([data-state=open])]:w-auto [&:has([data-state=open])]:opacity-100",
			)}
			ref={markerRef}
		>
			{itemLink ? (
				<DropdownMenu modal={false} onOpenChange={setIsDropdownOpen} open={isDropdownOpen}>
					{moreButtonWithTooltip}
					<DropdownMenuContent align="start" onClick={(e) => e.stopPropagation()} side="right">
						<EditMenu itemLink={currentItemLink} setItemLink={setCurrentItemLink} />
					</DropdownMenuContent>
				</DropdownMenu>
			) : (
				moreButtonWithTooltip
			)}
			{onAddChild && (
				<Tooltip>
					<TooltipTrigger asChild>
						<button
							aria-label={t("article.add-child")}
							className="hover:bg-sidebar-accent group/add m-0 flex size-5 appearance-none items-center justify-center rounded border-0 bg-transparent p-0 text-left text-muted-foreground"
							data-testid="create-article"
							onClick={onAddChild}
							type="button"
						>
							<Icon className="group-hover/add:text-primary-fg" icon="plus" />
						</button>
					</TooltipTrigger>
					<TooltipContent focus="high">{t("article.add-child")}</TooltipContent>
				</Tooltip>
			)}
		</span>
	);
};
